import React from "react";
import { Card, Table } from "react-bootstrap";
import type {
  PastorChurchReport,
  PastorLeaf,
  PastorSection,
} from "@core/reports/api/fetchPastorReport";

/**
 * Read-only replica of the paper "Minister's Monthly Report Form"
 * (.prompts/support/NTCOG - Ministers Report Form 2021.pdf), rendered from a single
 * church's pastor report. Reused by ChurchPastorReportPage and the per-church eyeball
 * modal (D6/D7). Wrapped in `.ministers-report-printable` for browser print (D15).
 */

function isLeaf(v: PastorLeaf | Record<string, PastorLeaf> | undefined): v is PastorLeaf {
  return !!v && typeof v === "object" && "value" in v && "label" in v;
}

function formatValue(leaf?: PastorLeaf): string {
  if (!leaf || leaf.value === null || leaf.value === undefined || leaf.value === "") return "—";
  const { value, data_type } = leaf;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const type = (data_type ?? "").toUpperCase();
  if (["CURRENCY", "DECIMAL", "FLOAT"].includes(type) && typeof value === "number") {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (["INTEGER", "INTEGER_UNSIGNED"].includes(type) && typeof value === "number") {
    return value.toLocaleString();
  }
  return String(value);
}

function leafOf(section: PastorSection, key: string): PastorLeaf | undefined {
  const v = section?.[key];
  return isLeaf(v) ? v : undefined;
}

type Props = {
  data: PastorChurchReport;
  /** Accepted for call-site compatibility (preview modal); the org header is no longer rendered on screen. */
  showHeader?: boolean;
};

const STATUS_FLAG_KEYS: Array<[string, string]> = [
  ["pastoring", "Pastoring"],
  ["evangelist", "Evangelist"],
  ["ordained_bishop", "Ordained Bishop"],
  ["ordained_minister", "Ordained Minister"],
  ["exhorter", "Exhorter"],
  ["full_time_minister", "Full Time Minister"],
  ["otherwise_employed", "Otherwise Employed"],
  ["other", "Other"],
];

const STATUS_OTHER_KEYS: Array<[string, string]> = [
  ["position", "Position"],
  ["vacation_leave", "Vacation Leave"],
  ["sick_leave", "Sick Leave"],
  ["other_leave_absence", "Other Leave/Absence"],
];

const FINANCE_KEYS: Array<[string, string]> = [
  ["gross_taxable_salary", "Gross Taxable Salary"],
  ["travelling_allowance", "Travelling Allowance"],
  ["rental_allowance", "Rental Allowance"],
  ["other_allowances", "Other Allowances"],
  ["total_income", "Total Income"],
  ["total_statutory_deductions", "Less: Total Statutory Deductions"],
  ["pension", "Pension"],
  ["expenses_in_ministry", "Expenses in Ministry"],
  ["other", "Other"],
  ["net_income", "Net Income for the Month"],
];

// Statistics rows: each section entry has a times_held / number_of_members leaf and optionally number_of_hours.
const STATISTIC_KEYS: string[] = [
  "pastoral_visits",
  "sermons",
  "water_baptism",
  "holy_ghost_baptism",
  "membership_addition",
  "funerals",
  "bible_study",
  "marriages",
  "lords_supper",
  "missions_activities",
  "administrative_meetings",
  "training_sessions",
  "counselling",
  "office",
  "community_service",
  "fasting",
  "baby_dedication",
  "church_pastors_council",
  "members_meeting",
];

const ASSESSMENT_KEYS: string[] = [
  "services_held",
  "services_attended",
  "attendance_factors",
  "church_growth_programmes",
  "general_state_of_church",
  "personal_growth",
  "personal_devotional",
  "nation_ministry_area",
  "special_remarks",
];

const MinistersReportView: React.FC<Props> = ({ data }) => {
  const { status, finances, statistics, assessment } = data;

  // Pull the primary (count) leaf + optional hours leaf for a statistics entry.
  const statRow = (key: string): { label: string; count?: PastorLeaf; hours?: PastorLeaf } | null => {
    const sub = statistics?.[key] as Record<string, PastorLeaf> | undefined;
    if (!sub || typeof sub !== "object") return null;
    const count = sub.times_held ?? sub.number_of_members;
    const hours = sub.number_of_hours;
    const label = count?.label ?? hours?.label ?? key;
    if (!count && !hours) return null;
    return { label, count, hours };
  };

  return (
    <div className="ministers-report-view">
      {/* STATUS */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Status</Card.Header>
        <Card.Body>
          <div className="row g-2">
            {STATUS_FLAG_KEYS.map(([key, label]) => (
              <div className="col-6 col-md-3" key={key}>
                <div className="d-flex justify-content-between border rounded px-2 py-1">
                  <span className="text-muted small">{label}</span>
                  <span className="fw-semibold">{formatValue(leafOf(status, key))}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="row g-2 mt-1">
            {STATUS_OTHER_KEYS.map(([key, label]) => {
              const leaf = leafOf(status, key);
              const isPosition = key === "position";
              return (
                <div className="col-6 col-md-3" key={key}>
                  <div
                    className={`d-flex border rounded px-2 py-1 ${
                      isPosition ? "flex-column gap-1" : "justify-content-between gap-2 align-items-start"
                    }`}
                  >
                    <span className="text-muted small">{label}</span>
                    <span className={`fw-semibold${isPosition ? "" : " text-end"}`}>
                      {formatValue(leaf)}
                      {leaf?.unit && key !== "position" && leaf.value != null ? ` ${leaf.unit}` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* FINANCES */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Finances</Card.Header>
        <Card.Body className="p-0">
          <Table responsive size="sm" className="mb-0 align-middle ntcog-table">
            <tbody>
              {FINANCE_KEYS.map(([key, label]) => (
                <tr key={key}>
                  <td>{label}</td>
                  <td className="text-end fw-semibold">{formatValue(leafOf(finances, key))}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* STATISTICS */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Statistics</Card.Header>
        <Card.Body className="p-0">
          <Table responsive size="sm" className="mb-0 align-middle ntcog-table">
            <thead className="table-light">
              <tr>
                <th>Activity</th>
                <th className="text-end" style={{ width: 90 }}>No.</th>
                <th className="text-end" style={{ width: 90 }}>Hrs</th>
              </tr>
            </thead>
            <tbody>
              {STATISTIC_KEYS.map((key) => {
                const row = statRow(key);
                if (!row) return null;
                return (
                  <tr key={key}>
                    <td>{row.label}</td>
                    <td className="text-end">{row.count ? formatValue(row.count) : "—"}</td>
                    <td className="text-end">{row.hours ? formatValue(row.hours) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ASSESSMENT */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Assessment</Card.Header>
        <Card.Body>
          {ASSESSMENT_KEYS.map((key) => {
            const leaf = leafOf(assessment, key);
            if (!leaf) return null;
            return (
              <div className="mb-3" key={key}>
                <div className="text-muted small">{leaf.label}</div>
                <div className="fw-medium" style={{ whiteSpace: "pre-wrap" }}>{formatValue(leaf)}</div>
              </div>
            );
          })}
        </Card.Body>
      </Card>
    </div>
  );
};

export default MinistersReportView;
